import React from 'react';
import { UserRound, Building2, ShieldCheck } from 'lucide-react';
import Reveal from './Reveal';

const ROLES = [
  {
    role: 'tenant',
    label: 'Tenant',
    Icon: UserRound,
    title: 'Tenants',
    benefit: 'Pay rent and keep every receipt in one place.',
    bullets: [
      'Pay via M-Pesa in seconds',
      'See invoices & balance anytime',
      'Download receipts & your agreement',
    ],
  },
  {
    role: 'landlord',
    label: 'Landlord',
    Icon: Building2,
    title: 'Landlords',
    benefit: 'Run your whole property without the paperwork.',
    bullets: [
      'Track rent collection in real time',
      'Manage tenants, agents & caretakers',
      'Monthly reports, exported in one click',
    ],
  },
  {
    role: 'admin',
    label: 'Admin',
    Icon: ShieldCheck,
    title: 'Admins',
    benefit: 'Oversee every property from a single dashboard.',
    bullets: [
      'Approve landlords & agents',
      'Full visibility across properties',
      'Reconcile every M-Pesa payment',
    ],
  },
];

function RoleCards() {
  return (
    <section style={styles.section}>
      <Reveal as="h2" style={styles.heading}>Who it's for</Reveal>
      <div style={styles.grid}>
        {ROLES.map(({ role, label, Icon, title, benefit, bullets }) => (
          <Reveal key={role} style={styles.card} className="card-lift">
            <div style={styles.iconWrap}><Icon size={22} /></div>
            <h3 style={styles.cardTitle}>{title}</h3>
            <p style={styles.benefit}>{benefit}</p>
            <ul style={styles.bulletList}>
              {bullets.map((b) => (
                <li key={b} style={styles.bullet}>
                  <span style={styles.bulletDot} />
                  {b}
                </li>
              ))}
            </ul>
            <a href={`/register?role=${role}`} style={styles.cardLink} className="link-underline">
              Register as {label} →
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const styles = {
  section: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '64px 24px',
  },
  heading: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(24px, 3vw, 32px)',
    fontWeight: 800,
    color: 'var(--color-ink)',
    textAlign: 'center',
    margin: '0 0 36px',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    justifyContent: 'center',
  },
  card: {
    flex: '1 1 280px',
    maxWidth: '340px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: '28px 26px',
    boxShadow: 'var(--shadow-sm)',
  },
  iconWrap: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    backgroundColor: 'var(--color-primary-light)',
    color: 'var(--color-primary)',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: 'var(--color-ink)',
    margin: '0 0 8px',
  },
  benefit: {
    fontSize: '14px',
    color: 'var(--color-ink-soft)',
    lineHeight: 1.5,
    margin: '0 0 16px',
  },
  bulletList: {
    listStyle: 'none',
    margin: '0 0 20px',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  bullet: {
    fontSize: '13px',
    color: 'var(--color-ink-soft)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  bulletDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary)',
    flexShrink: 0,
  },
  cardLink: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--color-primary)',
  },
};

export default RoleCards;
